import {
  getLocalTime,
  getUTCDateFromTime,
  isAfterDay,
} from '@gripp/shared-logic';
import { useCallback, useLayoutEffect, useState } from 'react';
import { useController, useWatch } from 'react-hook-form';
import { StyleProp, View, ViewStyle } from 'react-native';
import { Text } from 'react-native-paper';
import TimePicker from 'react-time-picker';
import 'react-time-picker/dist/TimePicker.css';
import './webDateTimePicker.css';

import { Icon } from '../../crop-circle/icons';
import { useFormContext } from '../formContext';
import { datePickerStyle } from './datePickerStyle';
import { DateRangeSelectorProps } from './dateRangeSelector';
import { Colors } from '../../../themes/colors';

const isSafari =
  typeof navigator !== 'undefined' &&
  /Safari/.test(navigator.userAgent) &&
  !/Chrome/.test(navigator.userAgent);

type WebDateTimePickerProps = DateRangeSelectorProps & {
  name: string;
  isStartDate?: boolean;
  label?: string;
  disabled?: boolean;
  /** Solid colour for the value while disabled, matching DatePickerSelector. */
  disabledTextColor?: string;
  /** Overrides the default sizing so the field can flex inside a row. */
  containerStyle?: StyleProp<ViewStyle>;
};

export const WebDateTimePicker = ({
  isStartDate = false,
  name,
  ...props
}: WebDateTimePickerProps) => {
  const {
    field: { value },
  } = useController({ name, control: props.control });
  const rangeItem = useWatch({
    control: props.control,
    name,
  });

  const [timeValue, setTimeValue] = useState<string | null>(
    value ? getLocalTime(value) : null
  );

  // Nullable by design: FormContext is shared-ui's own provider, and only the
  // startTime/endTime pairing below needs it. Screens that mount a standalone
  // picker (the reminder schedule-time field) render outside any provider, so
  // destructuring here instead would crash the whole form.
  const formContext = useFormContext();
  const getValues = formContext?.getValues;

  const updateTimeValue = useCallback(
    (fieldName: string, val: string) => {
      props.setValue(fieldName, val, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [props]
  );

  const handleChangeTime = useCallback(
    (time: string | null) => {
      setTimeValue(time);
      if (!time) return;
      const timeUTC = getUTCDateFromTime(time);
      if (isStartDate) {
        updateTimeValue(name, timeUTC);
        const endTime = getValues?.(name.replace('startTime', 'endTime'));
        if (!endTime || isAfterDay(timeUTC, endTime)) {
          updateTimeValue(name.replace('startTime', 'endTime'), timeUTC);
        }
        return;
      }
      if (name.includes('endTime')) {
        const startTime = getValues?.(name.replace('endTime', 'startTime'));
        if (startTime && isAfterDay(startTime, timeUTC)) {
          return;
        }
      }
      updateTimeValue(name, timeUTC);
    },
    [isStartDate, name, updateTimeValue, getValues]
  );

  // Keeps the displayed value in sync whenever the controlled field changes
  // for a reason other than this input's own onChange — e.g. a consumer
  // that mounts this picker before its RHF default is known (isolated forms
  // that `reset()` a computed baseline in an effect, like the alert/reminder
  // schedule-time pickers) only get a real value on a post-mount render,
  // which the mount-time useState initializer above already missed. Also
  // covers the pre-existing endTime case: reacting to a linked startTime
  // cascade from handleChangeTime.
  useLayoutEffect(() => {
    setTimeValue(rangeItem ? getLocalTime(rangeItem) : null);
  }, [rangeItem]);

  return (
    <View
      style={[
        datePickerStyle.timeContainerCropCircle,
        { width: isStartDate || name.includes('endTime') ? '48%' : '100%' },
        props.containerStyle,
      ]}
    >
      {props.label && (
        <View style={datePickerStyle.labelContainer}>
          <Text style={datePickerStyle.label}>{props.label}</Text>
        </View>
      )}
      {isSafari ? (
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            flex: 1,
            // Matches the standard input's inset (and the date fields').
            paddingLeft: 16,
          }}
        >
          <TimePicker
            onChange={handleChangeTime}
            value={timeValue}
            disabled={props.disabled}
            disableClock
            clearIcon={null}
            clockIcon={null}
            format="hh:mm a"
            className="gripp-time-picker"
          />
          <View style={{ marginLeft: 'auto', paddingRight: 8 }}>
            <Icon
              name="Clock B"
              size={18}
              color={
                props.disabled
                  ? (props.disabledTextColor ?? Colors.role.textStrong)
                  : Colors.role.textStrong
              }
            />
          </View>
        </View>
      ) : (
        <View style={{ flex: 1 }}>
          <input
            type="time"
            id={name}
            name={name}
            disabled={props.disabled}
            style={{
              border: 'none',
              WebkitAppearance: 'none',
              MozAppearance: 'textfield',
              backgroundColor: 'transparent',
              outline: 'none',
              // 14px matches the date fields this sits beside; the picker used
              // to render 2px larger than every date input in the app.
              fontSize: 14,
              padding: '0 0 0 16px',
              fontFamily:
                'Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif',
              fontWeight: 400,
              // Fill the row so a click anywhere in the field lands on the
              // input rather than the padding around it. border-box is load
              // bearing: a raw <input> defaults to content-box, where the
              // 16px inset above stacks onto width:100% and pushes the native
              // clock indicator out past the field's right edge.
              boxSizing: 'border-box',
              width: '100%',
              height: '100%',
              cursor: props.disabled ? 'default' : 'pointer',
              // Safari/Chrome grey out a disabled input's text differently;
              // pin it so a disabled value matches DatePickerSelector's.
              ...(props.disabled && props.disabledTextColor
                ? {
                    color: props.disabledTextColor,
                    WebkitTextFillColor: props.disabledTextColor,
                  }
                : {}),
            }}
            // Clicking a bare time input only focuses one segment — the picker
            // opens solely via its small clock affordance. The date field
            // opens its calendar on any click, so match that.
            onClick={(e) => {
              const input = e.currentTarget;
              if (input.disabled) return;
              try {
                input.showPicker?.();
              } catch {
                // Older browsers, or a click the engine doesn't count as user
                // activation — typing into the segments still works.
              }
            }}
            onChange={(e) => handleChangeTime(e.target.value || null)}
            value={timeValue ?? ''}
          />
        </View>
      )}
    </View>
  );
};
